import { IconActivityHeartbeat, IconBounceRight, IconCheck, IconEye, IconPlug, IconShieldLock } from "@tabler/icons-react";
import classNames from "classnames";
import { JSX, PropsWithoutRef } from "react";
import ServiceCard, { Service } from "~/components/Home/Services/ServiceCard";

export type ServicesProps = Omit<PropsWithoutRef<JSX.IntrinsicElements["section"]>, "children">;

const services: Array<Service> = [
  {
    description: "Uptime monitoring",
    detail: (
      <>
        Keep a close watch on your
        {" "}
        <span className="fw-medium">website and API</span>
        {" "}
        without the hassle. HTTP monitors
        {" "}
        <span className="fw-medium">automatically monitor</span>
        {" "}
        your infrastructure
        {" "}
        <span className="fw-medium">24/7</span>
        {", "}
        alerting you the moment something goes wrong.
        {" "}
        <span className="fw-medium">Simple to set up</span>
        {" "}
        and even easier to manage, they ensure you&apos;re the first to know about any downtime. Stay informed, stay ahead.
      </>
    ),
    icon: IconEye,
    iconWrapperClassName: "monitor-kind-http",
    link: "/services/uptime-monitoring",
    title: "HTTP monitoring",
  },
  {
    description: "Cron job monitoring",
    detail: (
      <>
        From
        {" "}
        <span className="fw-medium">database backups to routine updates</span>
        {", "}
        run smoothly and on time. Heartbeat monitors keeps an eye on your
        {" "}
        <span className="fw-medium">cron jobs</span>
        {", "}
        instantly notifying you if any task doesn&apos;t execute as planned. It&apos;s the
        {" "}
        <span className="fw-medium">effortless way</span>
        {" "}
        to guarantee your operations
        run without a hitch, providing
        {" "}
        <span className="fw-medium">peace of mind</span>
        {" "}
        and operational continuity. Perfect for keeping essential processes on track.
      </>
    ),
    icon: IconActivityHeartbeat,
    iconWrapperClassName: "monitor-kind-heartbeat",
    link: "/services/cron-job-monitoring",
    title: "Heartbeats",
  },
  {
    description: "Transparency made simple",
    detail: (
      <>
        Create
        {" "}
        <span className="fw-medium">custom status pages</span>
        {" "}
        that keep your customers informed with
        {" "}
        <span className="fw-medium">real-time updates</span>
        {", "}
        reinforcing
        {" "}
        <span className="fw-medium">trust and transparency</span>
        {". "}
        These pages are
        {" "}
        <span className="fw-medium">fully customizable</span>
        {" "}
        and
        {" "}
        <span className="fw-medium">effortlessly hosted</span>
        {" "}
        to reflect your brand, making communication during downtimes clear and effective. Simple setup for a major impact. Ensure your
        {" "}
        <span className="fw-medium">users are always in the loop</span>
        {", "}
        seamlessly.
      </>
    ),
    icon: IconCheck,
    iconWrapperClassName: "bg-primary",
    link: "/services/status-pages",
    title: "Status pages",
  },
  {
    description: "Keep your servers responsive",
    detail: (
      <>
        Use one of the most popular tools to check if your network devices are up. Keep track of your server&apos;s availability with our
        {" "}
        <span className="fw-medium">ping monitoring</span>
        {" "}
        service. You make sure your servers are reachable and working well, getting
        {" "}
        <span className="fw-medium">instant alerts</span>
        {" "}
        if there are any issues. Check the
        {" "}
        <span className="fw-medium">comprehensive report dashboard</span>
        {" "}
        to learn more about your
        {" "}
        <span className="fw-medium">ping performance</span>
        .
      </>
    ),
    icon: IconBounceRight,
    iconWrapperClassName: "monitor-kind-ping",
    link: "/services/ping-monitoring",
    title: "Ping monitoring",
  },
  {
    description: "Secure your network at every port",
    detail: (
      <>
        Keep your
        {" "}
        <span className="fw-medium">network devices</span>
        {" "}
        and services secure with
        {" "}
        <span className="fw-medium">real-time port monitoring</span>
        {". "}
        From FTP to SMTP, WebGazer keeps an eye on your critical ports to ensure your
        {" "}
        <span className="fw-medium">infrastructure stays responsive</span>
        {". "}
        Receive
        {" "}
        <span className="fw-medium">instant alerts</span>
        {" "}
        when issues arise, access
        {" "}
        <span className="fw-medium">detailed reports</span>
        {" "}
        for troubleshooting, and rest easy knowing your network is always protected.
      </>
    ),
    icon: IconPlug,
    iconWrapperClassName: "monitor-kind-port",
    link: "/services/port-monitoring",
    title: "Port monitoring",
  },
  {
    description: "Security without the stress",
    detail: (
      <>
        Make sure your website&apos;s
        {" "}
        <span className="fw-medium">SSL certificates</span>
        {" "}
        are always valid and up to date. With SSL monitoring, you get alerts before your certificates expire, helping you keep secure
        connections and protect your users&apos; data. Avoid the hassle of manual checks,
        reduce security risks, maintain user trust, and prevent compliance issues and potential downtime.
      </>
    ),
    icon: IconShieldLock,
    iconWrapperClassName: "bg-success",
    link: "/docs/http-monitors/introduction#ssl",
    title: "SSL monitoring",
  },
];

export default function Services({ className, ...props }: ServicesProps) {
  return (
    <section {...props} className={classNames("pb-40", className)}>
      <div className="bg-body-tertiary pb-64 pt-40">
        <div className="container">
          <div className="text-center">
            <h2 className="fs-3 fw-semi-bold">Ensure uptime with effortless monitoring</h2>

            <div className="fs-5 mt-8 text-body-secondary">
              Monitor websites, APIs, servers, and cron jobs with real-time alerts and comprehensive reports.
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-n40">
        <div className="gx-xl-20 gy-12 row row-cols-1 row-cols-lg-2">
          {services.map((s) => <ServiceCard key={s.title} service={s} />)}
        </div>
      </div>
    </section>
  );
}
